/**
 * The reading session: what lets this site's lookups and questions through.
 *
 * The server answers passage, lexicon and chat only for a browser holding a
 * reading session (`engine/visitor.py`), so they are the site's own and not a free
 * API for other programs. A session starts with one bot check and lasts weeks.
 *
 * Nothing here runs up front. A request goes out as normal; if the server answers
 * 401 `visitor_session_required`, the session is started once and the request is
 * sent again. A returning reader whose cookie is still live never sees any of
 * this, and one whose session lapsed meets it exactly when it matters.
 */

import { siteBootstrap } from './account'
import { turnstileToken } from '@shared/turnstile.js'

// standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
// Python constant, so the single home stays the server and this mirrors it. The
// paydown is generating the vocabulary into TypeScript rather than carrying a marker
// per site -- see docs/engineering-standards.md.
export const SESSION_REQUIRED = 'visitor_session_required'

/** One start in flight at a time, so a page firing five lookups passes one check. */
let starting: Promise<void> | null = null

/** Start a reading session: the bot check, then `POST /api/visitor`. */
export function startReadingSession(): Promise<void> {
  starting ??= (async () => {
    try {
      const { turnstileSiteKey } = await siteBootstrap()
      const token = await turnstileToken(turnstileSiteKey)
      const response = await fetch('/api/visitor', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ turnstile_token: token }),
      })
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}))
        throw new Error(detail.detail ?? response.statusText)
      }
    } finally {
      starting = null
    }
  })()
  return starting
}

/** Whether a failed response is the server asking for a reading session. */
async function asksForSession(response: Response): Promise<boolean> {
  if (response.status !== 401) return false
  const body = await response
    .clone()
    .json()
    .catch(() => ({}))
  return body.kind === SESSION_REQUIRED
}

/**
 * Send a request; if it needs a reading session, start one and send it once more.
 *
 * Once, not in a loop: a second 401 after a fresh session is a real failure and
 * is returned to the caller to show, never retried forever.
 */
export async function withReadingSession(send: () => Promise<Response>): Promise<Response> {
  const first = await send()
  if (!(await asksForSession(first))) return first
  await startReadingSession()
  return send()
}
